import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.css';

interface SidebarProps {
    isOpen: boolean;
    onMenuClick?: () => void; // 메뉴 클릭 시 콜백 추가
}

interface MenuItem {
    id: string;
    title: string;
    icon: string;
    path?: string;
    children?: MenuItem[];
    badge?: string;
}

const Sidebar = ({ isOpen, onMenuClick }: SidebarProps) => {
    const location = useLocation();
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

    const menuItems: MenuItem[] = [
        {
            id: 'dashboard',
            title: '대시보드',
            icon: 'ri-dashboard-line',
            path: '/dashboard',
        },
        {
            id: 'users',
            title: '사용자관리',
            icon: 'ri-group-line',
            children: [
                {
                    id: 'agency',
                    title: '대행사관리',
                    icon: '',
                    path: '/corporation',
                },
                {
                    id: 'farmer',
                    title: '농주관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'worker',
                    title: '작업자관리',
                    icon: '',
                    path: '/dev',
                },
            ],
        },
        {
            id: 'farmwork',
            title: '농작업관리',
            icon: 'ri-leaf-line',
            children: [
                {
                    id: 'parcels',
                    title: '필지관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'work',
                    title: '작업관리',
                    icon: '',
                    path: '/dev',
                },
            ],
        },

        {
            id: 'weather',
            title: '기상관리',
            icon: 'ri-sun-cloudy-line',
            children: [
                {
                    id: 'weather_data',
                    title: '기상데이터',
                    icon: '',
                    path: '/weather/',
                },
                {
                    id: 'weather_station',
                    title: '기상대관리',
                    icon: '',
                    path: '/weather/station',
                },
                {
                    id: 'weather_area',
                    title: '기상지역코드',
                    icon: '',
                    path: '/weather/area',
                },
                {
                    id: 'weather_schedule',
                    title: '기상스케줄관리',
                    icon: '',
                    path: '/weather/log',
                },
            ],
        },
        {
            id: 'growth',
            title: '생육관리',
            icon: 'ri-tree-fill',
            children: [
                {
                    id: 'season',
                    title: '생육작기관리',
                    icon: '',
                    path: '/season',
                },
                {
                    id: 'farmland',
                    title: '생육농지관리',
                    icon: '',
                    path: '/farmland',
                },
                {
                    id: '',
                    title: '작물코드관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: '',
                    title: '품종코드관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: '',
                    title: '생육단계관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: '',
                    title: '농작업매트릭스',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: '',
                    title: '실험데이터',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: '',
                    title: '생육대시보드',
                    icon: '',
                    path: '/growth/dashboard',
                },
            ],
        },
        {
            id: 'journal',
            title: '영농일지관리',
            icon: 'ri-calendar-2-line',
            path: '/dev',
        },
        {
            id: 'materials',
            title: '농자재관리',
            icon: 'ri-store-2-line',
            path: '/dev',
        },
        {
            id: 'commons',
            title: '공통관리',
            icon: 'ri-codepen-line',
            children: [
                {
                    id: 'crop-code',
                    title: '작물코드관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'work-code',
                    title: '농작업코드관리',
                    icon: '',
                    path: '/dev',
                },
            ],
        },
        {
            id: 'operation',
            title: '운영관리',
            icon: 'ri-booklet-line',
            children: [
                {
                    id: 'notice',
                    title: '공지사항관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'terms',
                    title: '약관관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'inquiry',
                    title: '1:1문의관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'faq',
                    title: 'FAQ관리',
                    icon: '',
                    path: '/dev',
                },
                {
                    id: 'notifi',
                    title: '알림관리',
                    icon: '',
                    path: '/dev',
                },
            ],
        },
        {
            id: 'statis',
            title: '통계',
            icon: 'ri-line-chart-line',
            children: [{ id: 'chat', title: '메뉴', icon: '', path: '/dev' }],
        },
        {
            id: 'system',
            title: '시스템관리',
            icon: 'ri-settings-2-line',
            children: [
                { id: 'chat', title: '메뉴', icon: '', path: '/dev' },
                { id: 'mail', title: '메뉴', icon: '', path: '/dev' },
            ],
        },
    ];

    // 경로와 메뉴 패스 매칭 함수 (동적 경로 지원)
    const isPathMatch = (menuPath: string, currentPath: string): boolean => {
        // 정확히 일치하는 경우
        if (menuPath === currentPath) return true;

        // 동적 경로 패턴 체크 (예: /corporation이 /corporation/123과 매칭)
        if (currentPath.startsWith(menuPath + '/')) {
            return true;
        }

        return false;
    };

    // 현재 경로에 해당하는 부모 메뉴를 찾는 함수
    const findActiveSubmenu = (pathname: string): string | null => {
        for (const item of menuItems) {
            if (item.children) {
                // 자식 메뉴들 중에 현재 경로와 일치하는 것이 있는지 확인
                const hasActiveChild = item.children.some(
                    child => child.path && isPathMatch(child.path, pathname),
                );
                if (hasActiveChild) {
                    return item.id;
                }
            } else if (item.path && isPathMatch(item.path, pathname)) {
                // 직접적인 경로 매치 (자식이 없는 메뉴)
                return null; // 단일 메뉴는 submenu를 열 필요 없음
            }
        }
        return null;
    };

    // 경로 변경 시 활성 메뉴 설정
    useEffect(() => {
        const activeMenu = findActiveSubmenu(location.pathname);
        setActiveSubmenu(activeMenu);
    }, [location.pathname]);

    const toggleSubmenu = (menuId: string) => {
        setActiveSubmenu(activeSubmenu === menuId ? null : menuId);
    };

    // 메뉴 클릭 핸들러 (실제 페이지 이동이 있는 메뉴만)
    const handleMenuClick = () => {
        onMenuClick?.();
    };

    // 공통 컴포넌트 추출
    const MenuBadge = ({ badge }: { badge: string }) => (
        <span className="badge bg-warning-transparent ms-2">{badge}</span>
    );

    const MenuLabel = ({ title }: { title: string }) => (
        <span className="side-menu__label">{title}</span>
    );

    const MenuIcon = ({ icon }: { icon: string }) => <i className={icon}></i>;

    // 클래스네임 생성 헬퍼 함수
    const getMenuItemClasses = (isActive: boolean) => ({
        menuItem: `side-menu__item ${isActive ? 'active' : ''}`,
        slideItem: `slide ${isActive ? 'open' : ''}`,
        submenu: `slide-menu child1 ${isActive ? 'active' : ''} ${isActive ? styles.open : ''}`,
    });

    const renderMenuItem = (item: MenuItem, level: number = 0) => {
        const { id, title, icon, path, children = [], badge } = item;
        const hasChildren = children?.length > 0;
        const isSubmenuActive = activeSubmenu === id;
        const classes = getMenuItemClasses(isSubmenuActive);

        if (!hasChildren) {
            const isUnderDevelopment = !path || path === '/dev';

            return (
                <li key={id} className="slide">
                    <NavLink
                        to={path || '#'}
                        className={() => {
                            // NavLink의 isActive는 정확한 매치만 감지하므로
                            // 우리의 커스텀 매칭 로직을 사용
                            const customActive =
                                path && isPathMatch(path, location.pathname);
                            return `side-menu__item ${customActive ? 'active' : ''}`;
                        }}
                        onClick={e => {
                            if (isUnderDevelopment) {
                                e.preventDefault(); // 페이지 이동 막기
                                alert('개발중입니다.');
                                return;
                            }
                            handleMenuClick(); // 정상 메뉴는 콜백 실행
                        }}
                    >
                        {icon && <MenuIcon icon={icon} />}
                        <MenuLabel title={title} />
                        {badge && <MenuBadge badge={badge} />}
                    </NavLink>
                </li>
            );
        }

        return (
            <li
                key={id}
                className={`slide has-sub ${isSubmenuActive ? 'open' : ''}`}
            >
                <a
                    href="#"
                    className={classes.menuItem}
                    onClick={e => {
                        e.preventDefault();
                        toggleSubmenu(id);
                    }}
                >
                    <MenuIcon icon={icon} />
                    <MenuLabel title={title} />
                    {badge && <MenuBadge badge={badge} />}
                    <i className="fe fe-chevron-right side-menu__angle" />
                </a>
                <ul className={classes.submenu}>
                    {children.map(child => renderMenuItem(child, level + 1))}
                </ul>
            </li>
        );
    };

    return (
        <aside
            className={`app-sidebar sticky ${isOpen ? 'open' : ''}`}
            id="sidebar"
        >
            <div className="main-sidebar-header">
                <a href="/" className="header-logo">
                    <img
                        src="/images/common/logo/logo_dark.png"
                        alt="logo"
                        className="desktop-logo"
                    />
                    <img
                        src="/images/common/logo/logo_short_dark.png"
                        alt="logo"
                        className="toggle-logo"
                    />
                    <img
                        src="/images/common/logo/logo_lite.png"
                        alt="logo"
                        className="desktop-white"
                    />
                    <img
                        src="/images/common/logo/logo_short_lite.png"
                        alt="logo"
                        className="toggle-white"
                    />
                </a>
            </div>

            <div className="main-sidebar" id="sidebar-scroll">
                <nav className="main-menu-container nav nav-pills flex-column sub-open">
                    <div className="slide-left" id="slide-left">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#7b8191"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"></path>
                        </svg>
                    </div>

                    <ul className="main-menu">
                        {/* Main Title */}
                        <li className="slide__category">
                            <span className="category-name">Main</span>
                        </li>

                        {/* Menu Items */}
                        {menuItems.map(item => renderMenuItem(item))}
                    </ul>

                    <div className="slide-right" id="slide-right">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#7b8191"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path d="m10.707 17.707 5.707-5.707-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"></path>
                        </svg>
                    </div>
                </nav>
            </div>
        </aside>
    );
};

export default Sidebar;
